import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  Link,
  Alert,
} from '@mui/material';
import { Close } from '@mui/icons-material';

interface TermsAndConditionsProps {
  open: boolean;
  onClose: () => void;
  onAccept?: () => void;
  showAcceptButton?: boolean;
  termsFile?: 'TERMS_AND_CONDITIONS.md' | 'EDITOR_TERMS_AND_CONDITIONS.md';
}

// The full, authoritative documents live on the marketing site. This dialog is
// a condensed version of them and must never contradict them: when the site
// terms change, update the summary below (and LAST_UPDATED) to match.
const FULL_TERMS_URL = 'https://pithplay.com/terms';
const EDITOR_TERMS_URL = 'https://pithplay.com/editor-terms';
const PRIVACY_POLICY_URL = 'https://pithplay.com/privacy';
const LAST_UPDATED = 'November 2, 2026';

interface TermsSection {
  heading: string;
  paragraphs?: React.ReactNode[];
  bullets?: React.ReactNode[];
}

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  // A new tab, so following a link never loses an in-progress signup.
  <Link href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </Link>
);

const MailLink: React.FC<{ address: string }> = ({ address }) => (
  <Link href={`mailto:${address}`}>{address}</Link>
);

const CREATOR_SECTIONS: TermsSection[] = [
  {
    heading: '1. PithPlay is in beta',
    bullets: [
      'PithPlay works and people use it every day, but it is still being built.',
      'Features may change or be removed, and you may run into bugs, slowdowns or outages.',
      <>We'd love your feedback at <MailLink address="info@artbits.solutions" />.</>,
    ],
  },
  {
    heading: '2. Who can use PithPlay',
    bullets: [
      'You must be at least 16 years old to create an account.',
      'To buy a paid plan, you must have reached the age of majority where you live (for example, 18 in Ontario).',
      'If you use PithPlay for an organization, you confirm you are allowed to accept these terms for it.',
    ],
  },
  {
    heading: '3. Your account',
    bullets: [
      'Give accurate information and keep your email address current.',
      'Keep your password private. You are responsible for what happens under your account.',
      <>If you think someone else has got into your account, tell us at <MailLink address="info@artbits.solutions" />.</>,
    ],
  },
  {
    heading: '4. Plans, billing and cancellation',
    bullets: [
      'Free: $0, forever. 1 GB storage, 3 active projects, 2 revision rounds per project. No card needed.',
      'Pro: $14.99 a month. 10 GB storage, 10 active projects, 5 revision rounds per project, and AI task suggestions. Quick edits: 60 minutes of 1080p exports and 100 editing-assistant prompts a month.',
      'Pro Max: $39.99 a month. 25 GB storage, unlimited active projects, 8 revision rounds per project, plus AI transcripts, editing from the transcript and suggested camera cuts. Quick edits: 300 minutes of exports a month, up to 4K, and 500 editing-assistant prompts. Allowances count per calendar month and don’t carry over.',
      'Paid plans are billed monthly in Canadian dollars in Canada and in US dollars everywhere else, and renew automatically until you cancel. Once you have paid in one currency, your plan stays in it. Sales tax is added at checkout based on your billing address. There is no annual billing or free trial of paid plans right now.',
      'Each video can be up to 1 GB on Free, 10 GB on Pro and 25 GB on Pro Max. The same limit applies to revisions your editor uploads to your projects. Storage can go up to 10% over your plan before new uploads are blocked.',
      'Payments are processed by Stripe. PithPlay never sees or stores your full card number.',
      'Upgrades take effect immediately, and your card is charged the prorated difference for the rest of the current period, plus tax.',
      'Downgrades take effect at the end of your current billing period. You keep the higher plan until then, with no refund or credit.',
      'Cancelling stops future charges. You keep paid features until the end of the period you paid for, then move to the Free plan.',
      'We never delete your data because you downgrade or cancel. If you are over the new limits, you can’t add more until you are back under them.',
      'If a payment fails, Stripe retries it. If it keeps failing, access to paid features may be limited.',
      'Payments are non-refundable, except where the law requires a refund or where we decide to give one at our discretion.',
      'If prices change, we’ll tell you before the new price applies to your next renewal.',
    ],
  },
  {
    heading: '5. Your content',
    bullets: [
      'You own what you upload: videos, images, audio, reference files, annotations, briefs, notes and revisions.',
      'You give Artbits Solutions Ltd. a limited licence to host, process, display and transmit your content only as needed to run PithPlay for you, including transcripts of the speech in your videos and AI suggestions when you or your editor use them. We don’t sell your content or use it to train AI models.',
      'You must own your content or have every permission needed to upload it and let your editor work with it.',
      'Your project content is visible to the editor you assign, and their uploads and notes are visible to you. Download links expire (handoff links after 7 days).',
      'PithPlay isn’t a backup service. Please keep your own copies of anything important.',
    ],
  },
  {
    heading: '6. Acceptable use',
    paragraphs: ['Don’t use PithPlay to:'],
    bullets: [
      'upload anything illegal, or anything that infringes someone else’s rights;',
      'upload sexually explicit material, or anything that exploits or endangers children;',
      'harass, threaten or discriminate against anyone;',
      'upload malware, break or test our security, access accounts or projects that aren’t yours, or get around limits; or',
      'copy, resell or reverse engineer PithPlay, or send spam.',
    ],
  },
  {
    heading: '7. AI features',
    bullets: [
      'AI task suggestions come with Pro and Pro Max, and are optional. Nothing is sent to the AI service unless someone on the project chooses to use them.',
      'When used, project text (brief, annotation text, task text, timestamps, asset notes and links to attached files) is sent to Microsoft Azure OpenAI. Video and audio files aren’t sent for suggestions.',
      'This runs under Microsoft’s enterprise terms, which do not permit using customer data to train their models.',
      'On Pro Max, transcripts of the speech in your videos are made by speech recognition (OpenAI’s Whisper model) that Cloudflare Workers AI runs for us: when someone on the project asks for one, and automatically for a multicam project’s sound camera when it’s sent to an editor. The audio isn’t sent to OpenAI, and Cloudflare doesn’t use it to train AI models.',
      'Quick edits let you trim a video, add text, your logo, captions, music and a spoken line, reframe it for Shorts and export the result. Every plan can use the editor; exports and the editing assistant come with Pro and Pro Max, within the plan’s monthly allowance. Exports stay downloadable for 30 days.',
      'The editing assistant is optional. When you ask it for a change, the words of your instruction, the video’s transcript, the names of your brand-kit files and your current edit are sent to Microsoft Azure OpenAI; never your video, pictures or sounds. A spoken instruction is turned into text by Cloudflare Workers AI, and a line you ask to have spoken is generated by Microsoft Azure Speech from the text of that line. The assistant never exports on its own, and every change it makes can be undone. The text of your instructions isn’t kept.',
      'AI suggestions, transcripts and the assistant’s changes can be wrong. Review them before relying on them.',
    ],
  },
  {
    heading: '8. Creators and editors',
    paragraphs: [
      'Editors join by invitation and are never charged. Any arrangement between you and an editor (scope, payment, deadlines, who owns the final edit) is between the two of you. PithPlay doesn’t take a cut, isn’t part of that agreement and doesn’t guarantee either side’s work or payment.',
    ],
  },
  {
    heading: '9. Leaving, or if we need to act',
    bullets: [
      'You can cancel a paid plan at any time from the billing page.',
      <>
        There’s no self-serve account deletion yet. Email <MailLink address="info@artbits.solutions" /> and we’ll delete
        your account and content within 30 days, except records the law makes us keep, such as invoices.
      </>,
      'We may suspend or close accounts that break these terms or the law, or that put others at risk. Where reasonable, we’ll warn you first.',
    ],
  },
  {
    heading: '10. Disclaimer and limitation of liability',
    paragraphs: [
      'Technology changes fast, and PithPlay is in beta. We’ve taken reasonable steps to keep your data safe and private, but no software is perfect or error-free, and no system is completely secure. PithPlay is provided “as is” and “as available”.',
      'To the maximum extent the law allows, Artbits Solutions Ltd. isn’t liable for indirect or consequential losses, lost profits or data, or interruptions, and our total liability for any claim is limited to the greater of the amount you paid us in the 12 months before the claim or CA$50. Some places don’t allow these limits, so they apply only as far as your local law permits.',
    ],
  },
  {
    heading: '11. Changes and governing law',
    bullets: [
      'We’ll tell you by email or in the app before material changes to these terms take effect.',
      'These terms are governed by the laws of Ontario and the federal laws of Canada that apply there.',
    ],
  },
];

const EDITOR_SECTIONS: TermsSection[] = [
  {
    heading: '1. How editor access works',
    bullets: [
      'Editors join by invitation from a creator. Invitation links expire after 7 days.',
      'PithPlay never charges editors. The creator’s plan covers the project.',
      'You can see and work on only the projects a creator assigns to you. The creator decides who works on their projects and can remove you or reassign a project at any time.',
      'If you want to run your own projects, you can also register as a creator.',
    ],
  },
  {
    heading: '2. You’re independent',
    bullets: [
      'You’re an independent professional, not an employee, contractor or agent of PithPlay or Artbits Solutions Ltd.',
      'You’re responsible for your own taxes, insurance and any licences your work needs.',
    ],
  },
  {
    heading: '3. Your arrangement with the creator',
    paragraphs: [
      'Scope, payment, deadlines and who owns the edited video are for you and the creator to agree. PithPlay doesn’t take a cut, doesn’t process payments between you, isn’t part of your agreement, doesn’t guarantee either side’s work or payment, and doesn’t mediate disputes. We recommend putting your agreement in writing.',
    ],
  },
  {
    heading: '4. Looking after creators’ content',
    bullets: [
      'The creator owns what they upload. Working on a project doesn’t give you ownership of it.',
      'Keep creators’ content confidential and use it only for the project you’re assigned. Don’t upload it to other services (including other AI tools) unless the creator agrees.',
      'Delete any local copies when the project ends, unless the creator agrees you can keep them.',
    ],
  },
  {
    heading: '5. What you upload',
    bullets: [
      'Files and notes you upload to a project are visible to that project’s creator.',
      'You must have the rights to anything you add, such as music, stock footage, fonts or graphics, for the creator’s intended use.',
      'PithPlay isn’t a backup service. Keep your own copies of your work.',
    ],
  },
  {
    heading: '6. AI features',
    bullets: [
      'AI task suggestions are optional. When used, project text (not video or audio files) is sent to Microsoft Azure OpenAI, under Microsoft’s enterprise terms, which do not permit using customer data to train their models.',
      'Projects can include transcripts of the speech in the creator’s videos, made by speech recognition that Cloudflare runs for PithPlay. They can contain mistakes, so check names and quotes against the footage.',
      'A creator can send you a quick edit they made in PithPlay (its cuts, text, logo, captions and sound, and the exported file) as the starting point for a project. Treat it like the rest of the project’s content.',
      'AI suggestions can be wrong. You stay responsible for your edits.',
    ],
  },
  {
    heading: '7. Disclaimer and limitation of liability',
    paragraphs: [
      'PithPlay is in beta and provided “as is”. We’ve taken reasonable steps to keep data safe, but no software is perfect or error-free. To the maximum extent the law allows, we aren’t liable for indirect losses, lost income or data, or interruptions. Because editors are never charged, our total liability to you as an editor is limited to CA$50, to the extent the law allows.',
    ],
  },
];

const renderSection = (section: TermsSection) => (
  <Box key={section.heading} component="section">
    <Typography variant="h2">{section.heading}</Typography>
    {section.paragraphs?.map((paragraph, index) => (
      <Typography key={index} variant="body2" paragraph>
        {paragraph}
      </Typography>
    ))}
    {section.bullets && (
      <Box component="ul" sx={{ pl: 3, mb: 2, mt: 0 }}>
        {section.bullets.map((bullet, index) => (
          <Typography key={index} component="li" variant="body2" sx={{ mb: 0.5 }}>
            {bullet}
          </Typography>
        ))}
      </Box>
    )}
  </Box>
);

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  open,
  onClose,
  onAccept,
  showAcceptButton = false,
  termsFile = 'TERMS_AND_CONDITIONS.md',
}) => {
  const isEditorTerms = termsFile === 'EDITOR_TERMS_AND_CONDITIONS.md';
  const handleAccept = () => {
    if (onAccept) {
      onAccept();
    }
    onClose();
  };

  const sections = isEditorTerms ? EDITOR_SECTIONS : CREATOR_SECTIONS;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          {isEditorTerms ? 'Editor Terms and Conditions' : 'PithPlay Terms and Conditions'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Last updated:</strong> {LAST_UPDATED}<br />
            <strong>Effective date:</strong> {LAST_UPDATED}<br />
            <strong>Status:</strong> PithPlay is in beta
          </Typography>
        </Box>

        <Typography variant="body2" paragraph>
          {isEditorTerms ? (
            <>
              These Editor Terms add to the main PithPlay Terms and Conditions. They are an agreement between you and{' '}
              <strong>Artbits Solutions Ltd.</strong> (Ontario, Canada), which runs PithPlay. This is a short version.
              By accepting, you agree to the full <ExternalLink href={EDITOR_TERMS_URL}>Editor Terms</ExternalLink>, the
              full <ExternalLink href={FULL_TERMS_URL}>Terms and Conditions</ExternalLink> and our{' '}
              <ExternalLink href={PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink>.
            </>
          ) : (
            <>
              These terms are an agreement between you and <strong>Artbits Solutions Ltd.</strong> (Ontario, Canada),
              which runs PithPlay. This is a short version. By accepting, you agree to the full{' '}
              <ExternalLink href={FULL_TERMS_URL}>Terms and Conditions</ExternalLink> and our{' '}
              <ExternalLink href={PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink>, which say the same things in more
              detail.
            </>
          )}
        </Typography>

        <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Please read this part.</strong> Technology changes fast, and PithPlay is in beta. We’ve taken
            reasonable steps to keep your data safe and private, but no software is perfect or error-free, and no system
            is completely secure. Please keep your own copies of anything important. To the extent the law allows, we
            aren’t liable for indirect losses, lost profits or data, or interruptions, and our total liability is capped.
            By using PithPlay, you accept it on this understanding.
          </Typography>
        </Alert>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ '& h2': { fontSize: '1.15rem', fontWeight: 600, mt: 3, mb: 1.5 } }}>
          {sections.map(renderSection)}

          <Typography variant="h2">{isEditorTerms ? '8. Contact' : '12. Contact'}</Typography>
          <Typography variant="body2" paragraph>
            <strong>Artbits Solutions Ltd.</strong>, Ontario, Canada<br />
            Questions about these terms: <MailLink address="info@artbits.solutions" /> or{' '}
            <MailLink address="info@pithplay.com" />
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            Read the full versions on pithplay.com:{' '}
            {isEditorTerms && (
              <>
                <ExternalLink href={EDITOR_TERMS_URL}>Editor Terms</ExternalLink>
                {' · '}
              </>
            )}
            <ExternalLink href={FULL_TERMS_URL}>Terms and Conditions</ExternalLink>
            {' · '}
            <ExternalLink href={PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', mt: 1 }}>
            © 2026 Artbits Solutions Ltd. All rights reserved.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {showAcceptButton ? (
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Button onClick={onClose} variant="outlined" sx={{ flex: 1 }}>
              Decline
            </Button>
            <Button onClick={handleAccept} variant="contained" sx={{ flex: 1 }}>
              I Accept
            </Button>
          </Box>
        ) : (
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TermsAndConditions;
